<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { useRouter } from 'vue-router/composables'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'
import { novelmelloRankingTheme as ui } from '../configs/novelmello'
import { useGotoBack } from '@/composables'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

/**
 * NovelMello 榜单页视图层
 *
 * ⚠️ props 契约与 RankingPage.default.vue 完全一致，数据 / 埋点 / 跳转语义不变，
 * 仅换皮：粉紫渐变头部氛围 + 白色大卡 + CSS 数字名次徽章。
 */
interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const router = useRouter()
// 与 BasicNavbar 内部同一个返回逻辑（历史栈回退，兜底回首页）
const gotoBack = useGotoBack()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
} = useRanking(props.columnId, props.config)

function gotoBook(id: string, firstChapterId?: string) {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: id, chapterId: firstChapterId || '' },
  })
}

/** 名次徽章：前三名主色，第 4 名起用粉 soft 底 */
function rankStyle(index: number) {
  if (index === 0)
    return { background: ui.rank1Bg, color: ui.rankTopText }
  if (index === 1)
    return { background: ui.rank2Bg, color: ui.rankTopText }
  if (index === 2)
    return { background: ui.rank3Bg, color: ui.rankTopText }

  return { background: ui.rankRestBg, color: ui.rankRestText }
}

/** chips：只取 tagList 前 N 个 name（与 default 行为一致，不含连载状态） */
function chipsOf(book: any) {
  return (book.tagList ?? [])
    .slice(0, ui.chipLimit)
    .map((tag: any) => tag?.name)
    .filter(Boolean)
}

function chipStyle(index: number) {
  const pink = index % 2 === 0
  return {
    background: pink ? ui.chipPinkBg : ui.chipPurpleBg,
    color: pink ? ui.chipPinkText : ui.chipPurpleText,
  }
}
</script>

<template>
  <div class="nm-ranking relative h-100dvh flex flex-col overflow-hidden">
    <!-- 顶部氛围渐变：绝对定位铺底，不拦截点击 -->
    <div class="nm-halo absolute left-0 right-0 top-0 h-140px pointer-events-none" />

    <!-- 顶栏：左侧返回 + 居中标题（不改公共 BasicNavbar，返回逻辑复用 useGotoBack） -->
    <header class="relative z-1 flex-shrink-0 h-48px flex items-center px-12px">
      <button
        class="nm-back flex items-center bg-transparent border-0 p-0"
        type="button"
        aria-label="Back"
        @click="gotoBack()"
      >
        <i-lucide-chevron-left class="w-24px h-24px" :style="{ color: ui.ink1 }" />
      </button>
      <h1
        class="nm-title absolute left-1/2 -translate-x-1/2 max-w-60% text-18px lh-24px font-700 truncate"
        :style="{ color: ui.ink1 }"
      >
        {{ title || '' }}
      </h1>
    </header>

    <!-- 白色大卡：列表滚动容器（无限滚动挂在这里） -->
    <div
      ref="infiniteRef"
      class="nm-card relative z-1 flex-1 overflow-y-auto mx-12px mb-12px rounded-20px px-12px py-16px"
      :style="{ background: ui.cardBg, boxShadow: ui.cardShadow }"
    >
      <div
        v-for="(book, index) in collections"
        :key="index"
        class="flex items-center"
        :class="index === 0 ? '' : 'mt-16px'"
        @click="gotoBook(book.id, book.firstChapter?.id)"
      >
        <!-- 名次徽章：CSS 数字，不渲染图片 -->
        <div
          class="nm-rank flex-shrink-0 w-24px h-24px rounded-8px flex items-center justify-center text-12px font-800"
          :style="rankStyle(index)"
        >
          {{ index + 1 }}
        </div>

        <div class="flex-shrink-0 w-60px h-80px rounded-10px overflow-hidden ml-12px">
          <BizBookCover
            :id="uuid()"
            :src="book.contentCoverUrl || ''"
            :extra="{
              module_name: books.showName,
              module_id: books.id,
              module_type: books.columnType,
              author: book.authorInfo.author,
              ...book,
            }"
            class="w-full h-full"
          />
        </div>

        <div class="ml-12px min-w-0 flex-1">
          <p class="text-14px font-800 line-clamp-1" :style="{ color: ui.ink1 }">
            {{ book.contentName }}
          </p>

          <p class="nm-desc mt-4px text-12px line-clamp-2" :style="{ color: ui.ink2 }">
            {{ book.introduce }}
          </p>

          <!-- chips：粉紫 soft 交替 -->
          <div v-if="chipsOf(book).length" class="nm-chips mt-8px flex flex-wrap items-center">
            <span
              v-for="(chip, ci) in chipsOf(book)"
              :key="ci"
              class="nm-chip px-8px py-3px text-10px font-700 rounded-8px"
              :style="chipStyle(ci)"
            >{{ chip }}</span>
          </div>
        </div>
      </div>

      <div v-if="ending" class="py-15px text-13px text-center" :style="{ color: ui.ink3 }">
        {{ config.texts.noMore }}
      </div>
      <div
        v-else-if="fetching"
        class="py-15px flex items-center justify-center space-x-4 text-13px"
        :style="{ color: ui.ink3 }"
      >
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nm-ranking {
  background: #FBF6FB;
}

.nm-halo {
  background: linear-gradient(180deg, #FDE8F1 0%, #EDE4FD 60%, #FBF6FB 100%);
}

/* 标题字体：Baloo 2（--nm-font-display 声明在 htmls/novelmello.html） */
.nm-title {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', system-ui, sans-serif);
}

.nm-desc {
  line-height: 1.45;
}

/* 白卡内部滚动条隐藏，保持设计稿干净观感 */
.nm-card {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.nm-card::-webkit-scrollbar {
  display: none;
}

/*
 * flex gap 兼容性：项目 scan:flex-gap 会拦截 gap 写法，
 * chips 间距用 margin 实现（参考 scripts/scan-flex-gap.ts 规则）
 */
.nm-chips > .nm-chip {
  margin-right: 6px;
  margin-top: 4px;
}

.nm-chips > .nm-chip:last-child {
  margin-right: 0;
}
</style>
